/**
 * Shortcuts overlay - P2 OBS-17 (2026-05-27).
 *
 * Modal listing app-wide keyboard shortcuts. Opens on:
 *   - Shift + ?
 *   - Cmd/Ctrl + /
 *
 * Discoverable hint shown in TopBar ("?") and in the overlay itself.
 */
import { useEffect, useState } from 'react';
import { Keyboard, X } from 'lucide-react';

interface Shortcut {
  keys: string[];
  label: string;
}

interface ShortcutGroup {
  group: string;
  items: Shortcut[];
}

const SHORTCUTS: ShortcutGroup[] = [
  {
    group: 'Navigation',
    items: [
      { keys: ['⌘', 'K'], label: 'Open command palette · global search' },
      { keys: ['Shift', '?'], label: 'Show this shortcuts overlay' },
      { keys: ['⌘', '/'], label: 'Show this shortcuts overlay' },
      { keys: ['Esc'], label: 'Close any open modal / panel' },
    ],
  },
  {
    group: 'Workspace',
    items: [
      { keys: ['g', 'h'], label: 'Go to Home' },
      { keys: ['g', 'd'], label: 'Go to Discovery' },
      { keys: ['g', 'a'], label: 'Go to Activity' },
      { keys: ['g', 'p'], label: 'Go to Detection (Decisions)' },
      { keys: ['g', 'c'], label: 'Go to Compliance Center' },
      { keys: ['/'], label: 'Jump to a page from the sidebar' },
    ],
  },
  {
    group: 'Tables',
    items: [
      { keys: ['n'], label: 'Next page' },
      { keys: ['p'], label: 'Previous page' },
    ],
  },
];

const NAV_TARGETS: Record<string, string> = {
  h: '/',
  d: '/discovery',
  a: '/activity',
  p: '/detection/decisions',
  c: '/compliance',
};

export function ShortcutsOverlay(): JSX.Element {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let gPending = false;
    let gTimer: number | null = null;

    function handler(e: KeyboardEvent): void {
      const target = e.target as HTMLElement | null;
      const inInput =
        target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
      if (inInput || target?.isContentEditable) {
        return;
      }

      // Shift + ?
      if (e.shiftKey && e.key === '?') {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      // Cmd/Ctrl + /
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      // Esc closes overlay
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
        return;
      }

      // "g then <letter>" navigation shortcuts.
      if (e.key === 'g' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        gPending = true;
        if (gTimer !== null) window.clearTimeout(gTimer);
        gTimer = window.setTimeout(() => {
          gPending = false;
        }, 1200);
        return;
      }
      if (gPending && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const target = NAV_TARGETS[e.key.toLowerCase()];
        if (target) {
          e.preventDefault();
          window.location.pathname = target;
        }
        gPending = false;
        if (gTimer !== null) window.clearTimeout(gTimer);
      }
    }
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      if (gTimer !== null) window.clearTimeout(gTimer);
    };
  }, [open]);

  if (!open) return <></>;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      className="fixed inset-0 z-[999] flex items-center justify-center p-4"
      onClick={() => setOpen(false)}
    >
      <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-2xl rounded-xl border border-border/60 bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
          <div className="flex items-center gap-2">
            <Keyboard className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold">Keyboard shortcuts</h2>
          </div>
          <button
            type="button"
            aria-label="Close shortcuts overlay"
            onClick={() => setOpen(false)}
            className="rounded-md p-1 hover:bg-muted/40 text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 max-h-[70vh] overflow-y-auto">
          {SHORTCUTS.map((group) => (
            <div key={group.group}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                {group.group}
              </div>
              <ul className="space-y-1.5">
                {group.items.map((s) => (
                  <li
                    key={`${group.group}-${s.label}`}
                    className="flex items-center justify-between gap-3 text-xs"
                  >
                    <span className="text-muted-foreground flex-1">{s.label}</span>
                    <span className="flex gap-1">
                      {s.keys.map((k, i) => (
                        <kbd
                          key={`${s.label}-${i}`}
                          className="px-1.5 py-0.5 rounded border border-border/60 bg-muted/40 font-mono text-[10px]"
                        >
                          {k}
                        </kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-border/40 text-[10px] text-muted-foreground">
          Open this overlay anywhere with{' '}
          <kbd className="px-1 py-0.5 rounded border border-border/60 bg-muted/40 font-mono">
            Shift
          </kbd>{' '}
          +{' '}
          <kbd className="px-1 py-0.5 rounded border border-border/60 bg-muted/40 font-mono">
            ?
          </kbd>
          .
        </div>
      </div>
    </div>
  );
}
